import { useCallback, useMemo } from "react";

import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  useCancelNotificationTask,
  useMarkAllNotificationsSeen,
  useMarkNotificationSeen,
  useNotificationCounts,
  useNotifications,
} from "@/modern/components/notification/api/notifications.api";
import { notificationsService } from "@/modern/components/notification/api/notifications.service";
import type {
  Notification,
  NotificationConfigContext,
  NotificationCountParams,
  NotificationCountsResponse,
  NotificationFilter,
  NotificationSummaryModel,
} from "@/modern/components/notification/types";
import { openExecutedIncentivesEditor, navigateToViewByType } from "@/modern/utils";
import { ALERT_ENTITIES, VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { WIDGET_REGISTRY } from "@/other/constants/EntityManagerConfig";
import type ViewManager from "@/other/feature/views/ViewManager";
import { openGeneralWidgetFromGridRecord } from "@/other/utils/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { WidgetParamKeys } from "@/types/entity";

import {
  mapNotificationCounts,
  shouldIncludeNotificationInUpdates,
  toApiParams,
  toMarkAllSeenBody,
} from "../constants/config";
import {
  historyRangeToAlertDateBounds,
  type UpdatesHistoryRange,
  type UpdatesInboxMode,
} from "../utils/timeWindow";
import { buildNotificationSummary } from "../utils";
import { useNotificationScope } from "./useNotificationScope";

export interface UseNotificationPopupOptions {
  filter: NotificationFilter;
  searchQuery: string;
  /** When false (summary), skip inbox list — counts alone drive the summary. */
  listEnabled?: boolean;
  closePopup: () => void;
  /** Unread = unseen catch-up (no date). All = history window on `alert_date`. */
  inboxMode?: UpdatesInboxMode;
  /** Only applied when `inboxMode` is `all`. */
  historyRange?: UpdatesHistoryRange;
}

const EMPTY_COUNTS: NotificationCountsResponse = {
  total_count: 0,
  unread_count: 0,
  notable_count: 0,
  type_counts: [],
  category_counts: [],
  status_counts: [],
};

export function useNotificationPopup(options: UseNotificationPopupOptions) {
  const {
    filter,
    searchQuery,
    listEnabled = true,
    closePopup,
    inboxMode = "unread",
    historyRange,
  } = options;
  const { project, boardManager, user, viewManager, entityManager, editorHandler, runAfterBoardLoad } =
    useSwivuBryntumContext();
  const scope = useNotificationScope();
  const currentUserId = user?.id ?? undefined;

  // Unread: mine + unseen, no date. All: mine + alert_date window (seen and unseen).
  const sharedParams = useMemo<NotificationCountParams>(() => {
    const base: NotificationCountParams = {
      board_id: scope.isAccountView ? null : (scope.boardId ?? undefined),
      scope: "mine",
      ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
    };
    if (inboxMode === "unread") {
      return { ...base, is_seen: false };
    }
    const bounds = historyRangeToAlertDateBounds(
      historyRange ?? { kind: "last_7_days" },
    );
    return { ...base, ...bounds };
  }, [scope.boardId, scope.isAccountView, searchQuery, inboxMode, historyRange]);

  const listParams = useMemo<NotificationCountParams>(() => {
    const params: NotificationCountParams = {
      ...sharedParams,
      ...toApiParams(filter),
    };
    // Unread stays unseen-only even if a filter omits or overrides it.
    if (inboxMode === "unread") {
      params.is_seen = false;
    }
    return params;
  }, [filter, sharedParams, inboxMode]);

  const notableCountParams = useMemo<NotificationCountParams>(
    () => ({
      ...sharedParams,
      notable: true,
    }),
    [sharedParams],
  );

  const routineCountParams = useMemo<NotificationCountParams>(
    () => ({
      ...sharedParams,
      notable: false,
    }),
    [sharedParams],
  );

  // Single inbox query for the active view filter (including notable). Paginate on scroll — never prefetch all pages.
  const listQuery = useNotifications(listParams, { enabled: listEnabled });
  // Main surface: one count request for Notable, one for Routine (non-notable).
  const notableCountsQuery = useNotificationCounts(notableCountParams, { enabled: true });
  const routineCountsQuery = useNotificationCounts(routineCountParams, { enabled: true });
  const markSeenMutation = useMarkNotificationSeen();
  const markAllMutation = useMarkAllNotificationsSeen();
  const cancelMutation = useCancelNotificationTask();

  const notifications = useMemo(() => {
    const loaded = listQuery.data?.pages.flatMap((page) => page.results) ?? [];
    return loaded.filter(shouldIncludeNotificationInUpdates);
  }, [listQuery.data?.pages]);

  /**
   * Keep the mosaic on skeleton until the first successful fetch for this list.
   * Avoids a CaughtUp flash when `isLoading` flips false a frame before results
   * land, and ignores background polls once we already have a success state.
   */
  const isListPending = Boolean(
    listEnabled && notifications.length === 0 && !listQuery.isSuccess,
  );

  const markSeen = useCallback(
    (notification: Notification) => markSeenMutation.mutateAsync({ id: notification.id, is_seen: true }),
    [markSeenMutation],
  );

  const cancelTask = useCallback(
    (asyncTaskId: number) => cancelMutation.mutateAsync(asyncTaskId),
    [cancelMutation],
  );

  const navigate = useCallback(
    (target: { viewType: string | number; viewName: string; otherParams?: Record<string, unknown> }) => {
      navigateToViewByType(viewManager, target.viewType, target.viewName, target.otherParams);
    },
    [viewManager],
  );

  const openNotification = useCallback(
    (notification: Notification, target: Parameters<NonNullable<NotificationConfigContext["openNotification"]>>[1]) => {
      const reminder = project?.reminderStore?.getById?.(notification.id);
      const otherBoard =
        notification.board != null && notification.board !== scope.boardId
          ? (boardManager?.boardNameStore?.getById?.(notification.board) ?? null)
          : null;
      // The other board's last view is only known once it loads, so the open waits for that load.
      const openOnOtherBoard = (open: (loaded: { viewManager: ViewManager }) => void) => {
        if (!otherBoard || !boardManager) return;
        runAfterBoardLoad(Number(otherBoard.id), open);
        boardManager.changeSelectedBoard(otherBoard);
      };
      // My Swivu is a hub, not a working view: return to the user's last view so closing the drawer lands there.
      const leaveMySwivu = () => {
        if (scope.isAccountView) viewManager?.goToPreviousView?.();
      };
      const openEntity = (otherParams?: Record<string, unknown>) => {
        const entityType = reminder?.entityType ?? notification.type;
        const entityId = reminder?.entity?.id ?? notification.task ?? notification.resource ?? notification.client;
        const editor = ALERT_ENTITIES[entityType];
        if (!editor || entityId == null) return false;
        const open = () =>
          entityManager?.openEntityManager?.({ entityType: editor.entityManagerPage, entityId, ...(otherParams ? { otherParams } : {}) });
        if (otherBoard) {
          openOnOtherBoard(open);
          return true;
        }
        leaveMySwivu();
        open();
        return true;
      };
      switch (target.kind) {
        case "reminder-detail":
          openEntity({ [WidgetParamKeys.REMINDER.EDIT]: Number(reminder?.id ?? notification.id) });
          return;
        case "schedule":
          if (reminder?.entity) leaveMySwivu();
          if (reminder?.entity && openGeneralWidgetFromGridRecord(editorHandler, reminder.entity, WIDGET_REGISTRY.timeline.id, { idSuffix: "timeline-schedule", otherParams: { initialMode: "schedule" } })) return;
          openEntity();
          return;
        case "checklist":
          openEntity(target.checklistId == null ? undefined : { [WidgetParamKeys.CHECKLIST.EDIT]: target.checklistId });
          return;
        case "note":
          openEntity(target.noteId == null ? undefined : { [WidgetParamKeys.NOTE.EDIT]: target.noteId });
          return;
        case "file":
          openEntity(target.fileId == null ? undefined : { [WidgetParamKeys.FILE.EDIT]: target.fileId });
          return;
        case "lead": {
          const leadParams = target.leadId != null ? { [WidgetParamKeys.LEADS.EDIT]: target.leadId } : {};
          if (otherBoard) {
            openOnOtherBoard(({ viewManager: loadedViewManager }) =>
              navigateToViewByType(loadedViewManager, VIEW_TYPES.leads, "Leads", leadParams));
            return;
          }
          navigateToViewByType(viewManager, VIEW_TYPES.leads, "Leads", leadParams);
          return;
        }
        default:
          openEntity();
      }
    },
    [boardManager, editorHandler, entityManager, project, runAfterBoardLoad, scope.boardId, scope.isAccountView, viewManager],
  );

  const context = useMemo<NotificationConfigContext>(
    () => ({
      currentUser: currentUserId == null ? null : { id: currentUserId },
      project,
      boardManager,
      scope: {
        kind: scope.isAccountView ? "account" : "board",
        ...(scope.boardId != null ? { boardId: scope.boardId } : {}),
        visibility: "mine",
      },
      markSeen,
      cancelTask,
      getResult: notificationsService.getResult,
      navigate,
      openNotification,
      openIncentiveResults: async (notification, result) => {
        if (!notification.async_task) return;
        await openExecutedIncentivesEditor({
          tasks: [notification.async_task as AsyncTask],
          project,
          fetchResult: async () => result,
          // Empty outcomes used to clear the card. Seen state stays on the eye button.
          markSeen: async () => {},
          editorHandler,
        });
      },
      closePopup,
    }),
    [boardManager, cancelTask, closePopup, currentUserId, editorHandler, markSeen, navigate, openNotification, project, scope.boardId, scope.isAccountView],
  );

  const mappedNotableCounts = useMemo(
    () => mapNotificationCounts(notableCountsQuery.data ?? EMPTY_COUNTS),
    [notableCountsQuery.data],
  );

  const mappedRoutineCounts = useMemo(
    () => mapNotificationCounts(routineCountsQuery.data ?? EMPTY_COUNTS),
    [routineCountsQuery.data],
  );

  // Combined inbox totals for Ask / filters; Notable + Routine are partitioned by the two count queries.
  const mappedCounts = useMemo(
    () => ({
      total: mappedNotableCounts.total + mappedRoutineCounts.total,
      unread: mappedNotableCounts.unread + mappedRoutineCounts.unread,
      notable: mappedNotableCounts.total,
      categories: mappedRoutineCounts.categories,
      byCategory: mappedRoutineCounts.byCategory,
    }),
    [mappedNotableCounts, mappedRoutineCounts],
  );

  const summary: NotificationSummaryModel = useMemo(
    () => buildNotificationSummary(mappedCounts),
    [mappedCounts],
  );

  const markAllSeen = useCallback(() => {
    const body = toMarkAllSeenBody(filter);
    if (!body) {
      return Promise.resolve({ updated_count: 0 });
    }
    return markAllMutation.mutateAsync(body);
  }, [filter, markAllMutation]);

  return {
    notifications,
    context,
    summary,
    mappedCounts,
    mappedNotableCounts,
    mappedRoutineCounts,
    markAllSeen,
    isMarkingAll: markAllMutation.isPending,
    fetchNextPage: listQuery.fetchNextPage,
    hasNextPage: listQuery.hasNextPage,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    isLoading:
      notableCountsQuery.isLoading ||
      routineCountsQuery.isLoading ||
      isListPending,
    isListPending,
  };
}

export function useNotificationPopupBadgeCount(): number {
  const scope = useNotificationScope();
  const sharedParams = useMemo<NotificationCountParams>(
    () => ({
      board_id: scope.isAccountView ? null : (scope.boardId ?? undefined),
      scope: "mine",
      is_seen: false,
    }),
    [scope.boardId, scope.isAccountView],
  );

  // Same partition as the Updates popup so React Query shares two requests — never a third unfiltered counts call.
  const notableCounts = useNotificationCounts(
    { ...sharedParams, notable: true },
    { enabled: true },
  );
  const routineCounts = useNotificationCounts(
    { ...sharedParams, notable: false },
    { enabled: true },
  );

  return (
    (notableCounts.data?.unread_count ?? 0) +
    (routineCounts.data?.unread_count ?? 0)
  );
}
